import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nProvider";
import {
  Loader2, UserPlus, Plane, Hotel, CalendarDays, ClipboardCheck, FlaskConical, Car, Circle,
} from "lucide-react";

const ICONS = {
  registration: UserPlus,
  flight: Plane,
  hotel: Hotel,
  appointment: CalendarDays,
  treatment_plan: ClipboardCheck,
  lab_order: FlaskConical,
  trip: Car,
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function PatientJourney({ patientId }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/journey/${patientId}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>;
  if (!data) return null;

  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="patient-journey">
      <h3 className="font-heading text-lg font-medium text-navy mb-5">{t("journey.timeline")}</h3>
      {data.events.length === 0 ? (
        <p className="text-sm text-slate-400">{t("journey.noEvents")}</p>
      ) : (
        <ol className="relative border-l-2 border-slate-100 ml-3">
          {data.events.map((e, i) => {
            const Icon = ICONS[e.kind] || Circle;
            return (
              <li key={i} data-testid={`journey-event-${i}`} className="mb-6 ml-6 last:mb-0">
                <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-navy text-gold ring-4 ring-white">
                  <Icon className="h-3 w-3" />
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-800">{e.title}</p>
                  {e.status && <span className="text-[11px] uppercase tracking-wide text-slate-400 bg-slate-100 rounded px-1.5 py-0.5 capitalize">{(e.status || "").replace(/_/g, " ")}</span>}
                </div>
                {e.subtitle && <p className="text-sm text-slate-500">{e.subtitle}</p>}
                <p className="text-xs text-slate-400 mt-0.5">{fmt(e.when)}</p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
